import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, NavLink } from "react-router-dom";
import { PanelLeftOpen, Search } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useAuth } from "@/context/AuthContext";
import { CommandPalette } from "@/components/CommandPalette";
import { Rail, MODULES, SETTINGS, moduleFor } from "@/components/layout/Rail";
import { WorkPanel } from "@/components/layout/panels/WorkPanel";
import { InboxPanel } from "@/components/inbox/InboxList";
import { CustomersPanel, ProductsPanel, SettingsPanel } from "@/components/layout/panels/ModulePanels";
import { MobileTabBar } from "@/components/layout/MobileTabBar";
import { UserMenu } from "@/components/layout/UserMenu";
import { UserAvatar } from "@/components/common";
import { LosMark } from "@/components/Logo";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AssistantPanel } from "@/components/assistant/AssistantPanel";
import { ResizeHandle } from "@/components/layout/ResizeHandle";
import { PANELS, RAIL, readPref, writePref, readWidth, writeWidth, clampW } from "@/lib/layout";

const AI_KEY = "sk.assistant.open";
const PANELS_BY_MODULE = { oversikt: WorkPanel, work: WorkPanel, inbox: InboxPanel, customers: CustomersPanel, products: ProductsPanel, settings: SettingsPanel };
const NO_PANEL = new Set(["pipeline"]); // Tilbudsoversikt is itself the overview – the board gets the whole surface

function MobileTop({ onSearch, title }) {
  const { user } = useAuth();
  return (
    <header className="flex h-14 shrink-0 items-center justify-between px-3 lg:hidden" data-testid="mobile-bar">
      <NavLink to="/" className="inline-flex items-center gap-2 rounded-md px-1" aria-label="Skarpenord – Nytt arbeid" data-testid="mobile-logo-link"><LosMark className="h-7 w-7" /><span className="font-display text-[15px] font-bold tracking-[-0.02em]">{title}</span></NavLink>
      <div className="flex items-center gap-1">
        <button type="button" onClick={onSearch} className="flex h-10 w-10 items-center justify-center rounded-xl text-foreground/70 hover:bg-black/[0.05]" data-testid="command-palette-trigger-mobile-button" aria-label="Søk"><Search className="h-5 w-5" strokeWidth={1.9} /></button>
        <UserMenu testId="mobile-user-menu">
          <button type="button" className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/50" data-testid="mobile-user-menu-trigger" aria-label="Profil og meny"><UserAvatar name={user?.name} initials={user?.initials} color={user?.color} src={user?.avatar_url} size="md" className="h-8 w-8" /></button>
        </UserMenu>
      </div>
    </header>
  );
}

/**
 * Navigation left, work in the middle, details right. Rail (64 px) → one context panel (the module's list, or the case
 * assistant when it is open – never both) → content. The panel is resizable and collapsible; sizes are remembered per user.
 * ⌘⇧S hides the panel, ⌘1–6 jump between modules.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isXl = useMediaQuery("(min-width: 1440px)");
  const isCanvas = pathname.startsWith("/p/");
  const isFull = isCanvas || pathname.startsWith("/innboks");
  const mod = moduleFor(pathname);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // The panel is remembered per module: hiding it on Arbeid for focus must not hide the Innboks list, which *is* the inbox.
  const prefKey = `left.hidden.${mod.key}`;
  const [pref, setPref] = useState(() => readPref(prefKey, user)); // "1" | "0" | null – null lets the shell decide
  useEffect(() => { setPref(readPref(prefKey, user)); }, [prefKey, user]);
  const [leftW, setLeftW] = useState(() => readWidth(PANELS.left, user));
  const [assistW, setAssistW] = useState(() => readWidth(PANELS.assist, user));
  const [autoHide, setAutoHide] = useState(false);
  const [assistant, setAssistant] = useState(false); // the case page takes over the left panel while its assistant is open
  const [leftSlotEl, setLeftSlotEl] = useState(null);
  const [aiOpen, setAiOpen] = useState(() => localStorage.getItem(AI_KEY) === "1");
  const toggleAi = useCallback(() => setAiOpen((v) => { localStorage.setItem(AI_KEY, !v ? "1" : "0"); return !v; }), []);
  const [railOpen, setRailOpen] = useState(() => readPref("rail.expanded", user) === "1");
  const toggleRail = useCallback(() => setRailOpen((v) => { writePref("rail.expanded", user, !v ? "1" : "0"); return !v; }), [user]);
  const railWidth = railOpen ? RAIL.expanded : RAIL.compact;

  useEffect(() => { if (!isCanvas) { setAssistant(false); setAutoHide(false); } }, [isCanvas]);
  const noPanel = NO_PANEL.has(mod.key);
  const collapsed = assistant ? false : noPanel ? true : pref === "1" ? true : pref === "0" ? false : autoHide || (isCanvas && !isXl);
  const toggle = useCallback(() => {
    const next = collapsed ? "0" : "1";
    setPref(next);
    writePref(prefKey, user, next);
  }, [collapsed, user, prefKey]);
  const p = assistant ? PANELS.assist : PANELS.left;
  const width = assistant ? assistW : leftW;
  const resize = useCallback((w) => { const v = clampW(p, w); (assistant ? setAssistW : setLeftW)(v); writeWidth(p, user, v); }, [p, assistant, user]);
  const reset = useCallback(() => { (assistant ? setAssistW : setLeftW)(p.def); writeWidth(p, user, null); }, [p, assistant, user]);

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      if (k === "k") { e.preventDefault(); setPaletteOpen((v) => !v); }
      else if (e.shiftKey && k === "s") { e.preventDefault(); toggle(); }
      else if (k === "j" && !isCanvas) { e.preventDefault(); toggleAi(); }
      else if (k === ",") { e.preventDefault(); navigate(SETTINGS.to); }
      else if (/^[1-6]$/.test(e.key) && !e.shiftKey && !e.altKey) { const m = MODULES[Number(e.key) - 1]; if (m) { e.preventDefault(); navigate(m.to); } }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, toggleAi, navigate, isCanvas]);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const PanelComp = PANELS_BY_MODULE[mod.key] || WorkPanel;
  const showLeft = isDesktop && !collapsed;
  const ctx = useMemo(() => ({
    openPalette, panelCollapsed: collapsed, togglePanel: toggle, leftWidth: showLeft ? width : 0, leftPanelWidth: leftW, leftLocked: pref === "0", railWidth,
    leftSlot: { el: showLeft && assistant ? leftSlotEl : null, active: assistant, setActive: setAssistant, width: assistW },
    requestAutoHide: setAutoHide, assistantOpen: aiOpen, toggleAssistant: toggleAi,
  }), [openPalette, collapsed, toggle, showLeft, width, leftW, pref, railWidth, assistant, leftSlotEl, assistW, aiOpen, toggleAi]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[hsl(var(--sidebar))]" data-testid="app-shell">
      {isDesktop ? <Rail expanded={railOpen} onToggle={toggleRail} /> : null}
      {showLeft ? (
        <aside className="relative flex h-full shrink-0 flex-col bg-[hsl(var(--sidebar))]" style={{ width }} data-testid="left-panel" data-mode={assistant ? "assistant" : mod.key} data-width={width}>
          {assistant ? <div ref={setLeftSlotEl} className="flex h-full min-h-0 flex-col" data-testid="left-slot" /> : <PanelComp onCollapse={toggle} onSearch={openPalette} />}
          <ResizeHandle value={width} min={p.min} max={p.max} onResize={resize} onReset={reset} onCollapse={assistant ? undefined : toggle} label={assistant ? "Bredde på assistenten" : "Bredde på sidemenyen"} testId="left-panel-handle" />
        </aside>
      ) : null}
      <div className="relative flex min-w-0 flex-1 flex-col bg-white lg:border-l lg:border-black/[0.07]" data-testid="app-main">
        {isDesktop && collapsed && !isCanvas && !noPanel ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={toggle} className="glass absolute left-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-[10px] text-foreground/60 shadow-[0_1px_2px_hsl(var(--shadow-color)/0.08),0_0_0_1px_hsl(var(--shadow-color)/0.06)] hover:text-foreground" aria-label="Vis panel" data-testid="panel-open-button"><PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.8} /></button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} className="flex items-center gap-2 rounded-lg px-2.5 py-1 text-[12px]">Vis panel <span className="kbd">⌘⇧S</span></TooltipContent>
          </Tooltip>
        ) : null}
        {!isDesktop && !isCanvas ? <MobileTop onSearch={openPalette} title={mod.label} /> : null}
        <main id="main-scroll" className={isFull ? "min-h-0 flex-1 overflow-hidden" : "min-h-0 flex-1 overflow-y-auto scrollbar-thin lg:pt-2"}>
          <Outlet context={ctx} />
        </main>
        {!isDesktop && !isCanvas ? <MobileTabBar /> : null}
      </div>
      {isDesktop && !isCanvas && aiOpen ? (
        isXl ? <AssistantPanel onClose={toggleAi} /> : (
          <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-[hsl(var(--brand)/0.25)] backdrop-blur-[2px] animate-in fade-in-0 duration-200" onClick={toggleAi} data-testid="assistant-backdrop" />
            <div className="relative z-50 h-full w-[400px] max-w-full animate-in slide-in-from-right duration-200 shadow-float"><AssistantPanel onClose={toggleAi} className="lg:w-full" /></div>
          </div>
        )
      ) : null}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
